import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { addDays, endOfDay } from 'date-fns';
import Validation from '../../Validation/Validation';
import DayPicker from './DayPicker';
import DestinationPicker from './DestinationPicker';
import OpenDates from './OpenDates';
import TravelPrograms from './TravelPrograms';
import { MuiAdditionalConfigs } from '../../../theme';
import { calculateEndDate, getCalendarMinEndDate, getCalendarMinStartDate } from '../../../helpers/dates';
import { TRAVEL_MAX_AGE, TRAVEL_MAX_DAYS, TRAVEL_MIN_AGE } from '../../../constants/travelCalcConstants';
import MainTextField from '../../MUI/MainTextField';
import { updateCalculationFormAge } from '../../../helpers/age';

const Form = ({
  formProps,
  age,
  setAge,
  travelDetails,
  setSelectedLimit,
  setSelectedPlan,
  setRegion,
  setDestinations,
  setDays,
  setRange,
  setSelectedPackage,
  plansWithAllowedLimitRegionAndAge,
  showAgeInput,
  fullWidthElement,
  showTravelPlans,
  prices,
  isFetchingPrice,
  priceKeys,
  amount,
  defaultMinDays,
  hasOnlyLiteDisallowedCountry,
  onCoverageChange,
}) => {
  const { errors, setValue, clearError, setError, getValues } = formProps;

  const MIN_START_DATE = getCalendarMinStartDate();
  const MIN_END_DATE = getCalendarMinEndDate(defaultMinDays);

  const {
    region,
    selectedDestinations,
    range,
    days,
    selectedPackage,
    selectedPlan,
    selectedLimit,
  } = travelDetails;

  const updateAge = useCallback(
    (e) => {
      updateCalculationFormAge(e.target.value, setValue, setAge);
    },
    [setAge, setValue],
  );

  const updateDays = useCallback(
    (newDays, shouldValidate = true) => {
      setValue('days', newDays, shouldValidate);
      setDays(newDays);
    },
    [setDays, setValue],
  );

  const updateDatesOnDayChange = (selectedDays) => {
    let newStartDate;
    if (!range.start) {
      newStartDate = MIN_START_DATE;
      setValue('start', MIN_START_DATE, true);
      setRange({ start: MIN_START_DATE });
    } else {
      newStartDate = range.start;
    }

    if (selectedDays >= defaultMinDays && selectedDays <= TRAVEL_MAX_DAYS) {
      const date = addDays(newStartDate, selectedDays - 1);
      const newEnd = endOfDay(date);
      setRange({ end: newEnd });
      setValue('end', newEnd, true);
    } else {
      setRange({ end: null });
      setValue('end', null, true);
    }
  };

  const updateDatesAccordingPackage = useCallback(
    (newPackage, startDate) => {
      const newStartDate = (startDate || MIN_START_DATE);
      setValue('start', newStartDate, true);
      setRange({ start: newStartDate });
      let newEndDate = calculateEndDate(newStartDate, newPackage);
      newEndDate = endOfDay(newEndDate);
      setValue('end', newEndDate, true);
      setRange({ end: newEndDate });

      updateDays(newPackage.value);
    },
    [MIN_START_DATE, setRange, setValue, updateDays],
  );

  return (
    <Grid container spacing={4}>
      {showAgeInput && (
        <Grid item xs={12} sm={4}>
          <Validation errors={errors} name="age">
            <MainTextField
              label="personalInfo.age"
              value={age || ''}
              variant="outlined"
              type="number"
              size="small"
              fullWidth
              name="age"
              step={1}
              onChange={updateAge}
              InputProps={{
                inputProps: {
                  max: TRAVEL_MAX_AGE,
                  min: TRAVEL_MIN_AGE,
                },
              }}
              required
            />
          </Validation>
        </Grid>
      )}
      <Grid item xs={12} {...(fullWidthElement && { ...MuiAdditionalConfigs.fullWidth })}>
        <DestinationPicker
          formProps={{ errors, setValue, setError, clearError }}
          region={region}
          selectedDestinations={selectedDestinations}
          selectedLimit={selectedLimit}
          setRegion={setRegion}
          setDestinations={setDestinations}
          setSelectedPlan={setSelectedPlan}
          setSelectedLimit={setSelectedLimit}
          days={days}
          onCoverageChange={onCoverageChange}
        />
      </Grid>
      <Grid item xs={12}>
        <OpenDates
          range={range}
          selectedPackage={selectedPackage}
          setSelectedPackage={setSelectedPackage}
          updateDatesOnDayChange={updateDatesOnDayChange}
          updateDatesAccordingPackage={updateDatesAccordingPackage}
          hasOnlyLiteDisallowedCountry={hasOnlyLiteDisallowedCountry}
        />
      </Grid>
      <Grid item xs={12}>
        <DayPicker
          setValue={setValue}
          errors={errors}
          range={range}
          days={days}
          selectedPackage={selectedPackage}
          setDays={setDays}
          setRange={setRange}
          setSelectedPackage={setSelectedPackage}
          updateDatesAccordingPackage={updateDatesAccordingPackage}
          updateDatesOnDayChange={updateDatesOnDayChange}
          defaultMinDays={defaultMinDays}
          minStartDate={MIN_START_DATE}
          minEndDate={MIN_END_DATE}
        />
      </Grid>

      {showTravelPlans && (
        <Grid item xs={12}>
          <TravelPrograms
            plans={plansWithAllowedLimitRegionAndAge}
            region={region}
            type="travel"
            setSelectedPlan={setSelectedPlan}
            setSelectedLimit={setSelectedLimit}
            selectedPlan={selectedPlan}
            selectedLimit={selectedLimit}
            prices={prices}
            isFetchingPrice={isFetchingPrice}
            priceKeys={priceKeys}
            amount={amount}
            onCoverageChange={onCoverageChange}
            formProps={{ setError, clearError, getValues }}
          />
        </Grid>
      )}
    </Grid>
  );
};

Form.propTypes = {
  formProps: PropTypes.shape({
    errors: PropTypes.shape({
      age: PropTypes.shape({}),
      region: PropTypes.shape({}),
      start: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      end: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      days: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
    formState: PropTypes.shape({}),
    setValue: PropTypes.func,
    register: PropTypes.func,
    clearError: PropTypes.func,
    setError: PropTypes.func,
    getValues: PropTypes.func,
  }).isRequired,
  age: PropTypes.number,
  setAge: PropTypes.func,
  travelDetails: PropTypes.shape({
    prices: PropTypes.shape({}),
    region: PropTypes.string,
    selectedDestinations: PropTypes.arrayOf(PropTypes.shape({})),
    range: PropTypes.oneOfType([
      PropTypes.shape({}),
      PropTypes.shape({
        start: PropTypes.instanceOf(Date),
        end: PropTypes.instanceOf(Date),
      }),
    ]),
    days: PropTypes.number,
    selectedLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    selectedPlan: PropTypes.string,
    selectedPackage: PropTypes.shape({}),
    isFetchingPrice: PropTypes.bool,
  }).isRequired,
  setSelectedLimit: PropTypes.func.isRequired,
  setSelectedPlan: PropTypes.func.isRequired,
  setRegion: PropTypes.func.isRequired,
  setDestinations: PropTypes.func.isRequired,
  setDays: PropTypes.func.isRequired,
  setRange: PropTypes.func.isRequired,
  setSelectedPackage: PropTypes.func.isRequired,
  onCoverageChange: PropTypes.func.isRequired,
  plansWithAllowedLimitRegionAndAge: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showAgeInput: PropTypes.bool.isRequired,
  fullWidthElement: PropTypes.bool.isRequired,
  showTravelPlans: PropTypes.bool.isRequired,
  priceKeys: PropTypes.shape({}).isRequired,
  prices: PropTypes.shape({}).isRequired,
  isFetchingPrice: PropTypes.bool.isRequired,
  amount: PropTypes.number.isRequired,
  defaultMinDays: PropTypes.number.isRequired,
  hasOnlyLiteDisallowedCountry: PropTypes.bool.isRequired,
};

Form.defaultProps = {
  age: null,
  setAge: null,
};

export default Form;
